class TreeNode {
  constructor(key, value = key, parent = null) {
    this.uniqueId = Math.random().toString(16).slice(2);
    this.key = key;
    this.value = value;
    this.parent = parent;
    this.children = [];
  }

  get isLeaf() {
    return this.children.length === 0;
  }

  get hasChildren() {
    return !this.isLeaf;
  }

  addChild(key, value = key) {
    const newNode = new TreeNode(key, value, this);
    this.children.push(newNode);
    return newNode;
  }
}

class Tree {
  constructor(key = 'root', value = key) {
    this.root = new TreeNode(key, value);
  }

  *preOrderTraversal(node = this.root) {
    yield node;
    if (node.children.length) {
      for (let child of node.children) {
        yield* this.preOrderTraversal(child);
      }
    }
  }

  *postOrderTraversal(node = this.root) {
    if (node.children.length) {
      for (let child of node.children) {
        yield* this.postOrderTraversal(child);
      }
    }
    yield node;
  }

  insert(parentNodeKey, key, value = key) {
    for (let node of this.preOrderTraversal()) {
      if (node.key === parentNodeKey) {
        const newNode = new TreeNode(key, value, node);
        node.children.push(newNode);
        return newNode;
      }
    }
    return null;
  }

  remove(key) {
    for (let node of this.preOrderTraversal()) {
      const filtered = node.children.filter((c) => c.key !== key);
      if (filtered.length !== node.children.length) {
        node.children = filtered;
        return true;
      }
    }
    return false;
  }

  find(key) {
    for (let node of this.preOrderTraversal()) {
      if (node.key === key) return node;
    }
    return undefined;
  }

  findByUniqueId(uniqueId) {
    for (let node of this.preOrderTraversal()) {
      if (node.uniqueId === uniqueId) return node;
    }
    return undefined;
  }
}

class ProductPicker {
  constructor(
    options,
    choiceNodes,
    choicesElement,
    subChoicesElement,
    nextButtonElement,
    scrollContainerElement,
    mainImageElement,
    headingElement,
    loadingElement,
    choicesSeparation = null,
    choicePreSelection = null,
  ) {
    this.loadingElement = loadingElement;
    this.setLoadingElement();

    // html elements
    this.choicesElement = choicesElement;
    this.subChoicesElement = subChoicesElement;
    this.nextButtonElement = nextButtonElement;
    this.scrollContainerElement = scrollContainerElement;
    this.mainImageElement = mainImageElement;
    this.headingElement = headingElement;

    this.choiceNodes = choiceNodes;
    this.choicesSeparation = choicesSeparation;
    this.choicePreSelection = choicePreSelection;
    this.options = options ?? [];
    this.filteredOptions = this.applyChoicePreSelection(this.choicePreSelection, this.options);
  }

  create() {
    this.loadingElement.dispatchEvent(new Event('picker-loading'));
    this.createTree();
    if (this.startingChoiceNode.isLeaf) {
      this.loadingElement.dispatchEvent(new Event('picker-done-loading'));
      console.log('Error, we should be going straight to product instead of here!!!');
      const option = this.startingChoiceNode.value.options[0];
      this.activateNextButton(option.metafieldsCustom.url);
      window.location.replace(option.metafieldsCustom.url);
    }
    this.paintChoices(this.startingChoiceNode);
    if (this.startingChoiceNode.value.imageElement) {
      this.mainImageElement.replaceChildren(this.startingChoiceNode.value.imageElement);
    }
    this.loadingElement.dispatchEvent(new Event('picker-done-loading'));
  }

  createTree() {
    const tree = new Tree('root', {
      options: this.filteredOptions,
      selectorElement: null,
      imageElement: null,
      childrenGroupedBy: 'root',
    });
    this.createChildren(tree.root, this.choiceNodes, 0);
    this.decisionTree = tree;
    this.selectedNodesTree = null;
    this.startingChoiceNode = this.skipSingleChoiceNodes(this.decisionTree.root);
  }

  applyChoicePreSelection(choicePreSelection, options) {
    if (choicePreSelection && choicePreSelection.preSelectionChoiceNode && choicePreSelection.preSelection) {
      const filteredOptions = options.filter(
        (o) =>
          o.metafieldsCustom[choicePreSelection.preSelectionChoiceNode].toLowerCase() ===
          choicePreSelection.preSelection.toLowerCase(),
      );
      if (options.length != filteredOptions.length) {
        this.createRemoveFiltersButton();
      }

      return filteredOptions;
    }
    return options;
  }

  addChild(node, options, image, title, childrenGroupedBy) {
    const newNode = node.addChild(title);
    const imageElement = this.createImage(image);
    const selectorElement = this.createSelector(image, title, newNode.uniqueId);
    const option = this;
    selectorElement.addEventListener('click', function () {
      option.optionSelected(node, this.dataset.choiceKey, this.dataset.nodeId);
    });
    newNode.value = {
      options,
      selectorElement: selectorElement,
      imageElement: imageElement,
      childrenGroupedBy: childrenGroupedBy,
    };
    return newNode;
  }

  createChildren(choiceNode, choices, choiceDepth) {
    const choiceNodeOptions = choiceNode.value.options;
    // leaf node
    if (choiceNodeOptions.length <= 1) {
      return;
    }
    // create leaf nodes
    // We are on last node and need to just have product titles here instead of choice option titles
    // This means that something in the metafields of items is duplicated
    if (choiceDepth == choices.length - 1) {
      choiceNodeOptions.forEach((option) => {
        const image = option?.metafieldsCustom.image;
        this.addChild(choiceNode, [option], image, option.title, 'title');
      });
      return;
    }

    // if this is the root, we need to stay with the first choice 1 more time
    const nextChoiceDepth = choiceNode.parent == null ? choiceDepth : choiceDepth + 1;
    const nextChoice = choices[nextChoiceDepth];
    const choiceNodeOptionsGrouped = Map.groupBy(choiceNodeOptions, (o) => o.metafieldsCustom[nextChoice]);
    choiceNodeOptionsGrouped.forEach((options, option) => {
      const firstOption = options[0];
      const firstImage = firstOption?.metafieldsCustom.image;
      const title = option ? option : firstOption?.title;
      const newNode = this.addChild(choiceNode, options, firstImage, title, nextChoice);
      this.createChildren(newNode, choices, nextChoiceDepth);
    });
  }

  skipSingleChoiceNodes(node) {
    var multiChoiceNode = node;
    while (multiChoiceNode.hasChildren && multiChoiceNode.children.length === 1) {
      multiChoiceNode = multiChoiceNode.children[0];
    }
    return multiChoiceNode;
  }

  skipSingleChoiceNodesReverse(node) {
    var multiChoiceNode = node;
    while (multiChoiceNode.parent && multiChoiceNode.parent.children.length === 1) {
      multiChoiceNode = multiChoiceNode.parent;
    }
    return multiChoiceNode;
  }

  paintChoices(node) {
    const options = document.createElement('div');
    options.id = node.uniqueId;

    // This means we skipped some choices so we want to display the node, preselected, so the user knows where they are at when they start their selections
    // very relevant with pre-selection stuff
    if (this.choicePreSelection && this.choicePreSelection.preSelection && node.key != this.decisionTree.root.key) {
      const skippedChoiceInfo = document.createElement('div');
      skippedChoiceInfo.className = 'grid grid-cols-4 gap-2 w-full mt-4';
      // create a non clickable selector
      const preSelectedElement = this.createSelector(node.value.imageElement, node.key, node.uniqueId);
      this.highlightSelectorElement(preSelectedElement);
      preSelectedElement.style.cursor = 'default';
      skippedChoiceInfo.appendChild(preSelectedElement);
      const dividerElement = this.createDividerElement();
      options.appendChild(skippedChoiceInfo);
      options.appendChild(dividerElement);
    }

    options.append(...this.createOptionsGridElement(node));
    this.choicesElement.replaceChildren(options);
  }

  paintSubChoices() {
    const grids = [];
    for (const node of this.selectedNodesTree.preOrderTraversal()) {
      if (node.value.optionsGridElement) {
        const dividerElement = this.createDividerElement();
        grids.push(dividerElement);

        grids.push(node.value.optionsGridElement);
      }
    }
    this.subChoicesElement.replaceChildren(...grids);
  }

  addNodeToSelectedNodesTree(selectedNode, selectedNodeParent) {
    // we have to reverse back to the parent that is a multichoice node
    const multiChoiceParent = this.skipSingleChoiceNodesReverse(selectedNodeParent);
    const selectedParentSelectedNodesTreeNode = this.selectedNodesTree?.find(multiChoiceParent.uniqueId);
    if (selectedParentSelectedNodesTreeNode) {
      selectedParentSelectedNodesTreeNode.children = [];
      return selectedParentSelectedNodesTreeNode.addChild(selectedNode.uniqueId, {
        optionTitle: selectedNode.key,
        optionsGridElement: null,
      });
    } else {
      this.selectedNodesTree = new Tree(selectedNode.uniqueId, {
        optionTitle: selectedNode.key,
        optionsGridElement: null,
      });
      return this.selectedNodesTree.root;
    }
  }

  optionSelected(selectedNodeParent, choiceKey, nodeId) {
    // user is selecting an already selected option
    const alreadySelectedNode = this.selectedNodesTree?.find(nodeId);
    if (alreadySelectedNode) {
      const subChoices = alreadySelectedNode.value.optionsGridElement;
      if (subChoices)
        this.scrollContainerElement.scrollTo({ top: this.scrollContainerElement.scrollHeight, behavior: 'smooth' });

      return;
    }

    const selectedNode = selectedNodeParent.children.find(
      (child) => child.key === choiceKey && child.uniqueId === nodeId,
    );
    this.unhighlightAllChildrenSelectorElements(selectedNodeParent);
    this.highlightSelectorElement(selectedNode.value.selectorElement);
    this.mainImageElement.replaceChildren(selectedNode.value.imageElement);

    // Add choice then set the sub-options or if leaf the next button
    var newSelectedNodesTreeNode = this.addNodeToSelectedNodesTree(selectedNode, selectedNodeParent);
    // We want to remove any single choice nodes here in order to skip them
    var multiChoiceSelectedNode = this.skipSingleChoiceNodes(selectedNode);
    if (multiChoiceSelectedNode.isLeaf) {
      // clears any lingering subchoices from other choices
      this.paintSubChoices();
      const option = multiChoiceSelectedNode.value.options[0];
      this.activateNextButton(option.metafieldsCustom.url);
      this.scrollContainerElement.scrollTo({ top: this.scrollContainerElement.scrollHeight, behavior: 'smooth' });
    } else {
      this.resetNextButton();
      const optionsGridElement = document.createElement('div');
      optionsGridElement.id = multiChoiceSelectedNode.uniqueId;
      optionsGridElement.append(...this.createOptionsGridElement(multiChoiceSelectedNode));
      newSelectedNodesTreeNode.value.optionsGridElement = optionsGridElement;
      this.paintSubChoices();
      this.scrollContainerElement.scrollTo({ top: this.scrollContainerElement.scrollHeight, behavior: 'smooth' });
    }
  }

  createImage(image, className = '') {
    const imageElement = document.createElement('img');
    var masterWidth = 0;
    var masterHeight = 0;
    if (image) {
      // this is always using a 1:1 ratio
      if (image.width > image.height) {
        masterWidth = image.width;
        masterHeight = image.width;
      } else {
        masterWidth = image.height;
        masterHeight = image.width;
      }
      imageElement.src = image.src;
      if (image.alt != null) imageElement.alt = image.alt;
    }
    imageElement.width = masterWidth;
    imageElement.height = masterHeight;
    imageElement.className = className;
    imageElement.loading = 'lazy';
    imageElement.style.maxWidth = '100%';
    imageElement.style.maxHeight = '100%';
    imageElement.style.objectFit = 'contain';
    imageElement.style.objectPosition = 'center';
    return imageElement;
  }

  createSelector(image, title, nodeId) {
    const button = document.createElement('button');
    button.className = 'flex flex-col items-center rounded-xl w-full py-1 px-2 text-sm lg:text-lg gap-2 font-normal';
    button.dataset.choiceKey = title;
    button.dataset.nodeId = nodeId;
    const imageContainer = document.createElement('div');
    imageContainer.className = 'border-0 border-transparent';
    const imageElement = this.createImage(image, 'h-20 w-20 lg:h-40 lg:w-40');
    imageContainer.appendChild(imageElement);
    button.appendChild(imageContainer);
    const textEle = document.createTextNode(title);
    button.appendChild(textEle);
    return button;
  }

  createRemoveFiltersButton() {
    const button = document.createElement('button');
    button.className = 'c-btn c-btn--primary text-lg';
    button.id = 'picker-remove-filters-button';
    const textEle = document.createTextNode('Remove Filter(s)');
    button.appendChild(textEle);
    const some = this;
    button.addEventListener('click', function () {
      some.removeFiltersButtonSelected(this);
    });
    this.headingElement.appendChild(button);
  }

  removeFiltersButtonSelected(buttonElement) {
    buttonElement.className = `${buttonElement.className} not-checked-next-button`;
    this.resetNextButton();
    // reset the options and rebuild the tree
    this.filteredOptions = this.options;
    this.create();
  }

  createDividerElement() {
    const dividerElement = document.createElement('div');
    dividerElement.className = 'block h-0.5 bg-shopify-bg-dark w-full my-4 lg:my-12';
    return dividerElement;
  }

  createOptionsGridElement(node) {
    const options = [];
    const optionGridElement = document.createElement('div');
    optionGridElement.className = 'grid grid-cols-4 gap-2 w-full mt-4';
    const optionGridElementGrouped = document.createElement('div');
    optionGridElementGrouped.className = 'grid grid-cols-4 gap-2 w-full mt-4';
    node.children.forEach((child) => {
      if (
        this.choicesSeparation &&
        node.value.childrenGroupedBy.toLowerCase() === this.choicesSeparation.group.toLowerCase() &&
        this.choicesSeparation.filter.map((f) => f.toLowerCase()).includes(child.key.toLowerCase())
      ) {
        optionGridElementGrouped.appendChild(child.value.selectorElement);
      } else {
        optionGridElement.appendChild(child.value.selectorElement);
      }
    });

    if (optionGridElement.children.length > 0) {
      options.push(optionGridElement);
    }

    if (optionGridElementGrouped.children.length > 0) {
      const text = document.createTextNode(this.choicesSeparation.title);
      const groupedTitleElement = document.createElement('p');
      groupedTitleElement.appendChild(text);
      groupedTitleElement.className = 'text-shopify-brand text-md lg:text-lg mt-4';
      options.push(groupedTitleElement);
      options.push(optionGridElementGrouped);
    }
    return options;
  }

  unhighlightAllChildrenSelectorElements(node) {
    if (node.hasChildren) {
      node.children.forEach((child) => {
        this.unhighlightSelectorElement(child.value.selectorElement);
        this.unhighlightAllChildrenSelectorElements(child);
      });
    } else {
      return;
    }
  }

  highlightSelectorElement(selectorElement) {
    // button text
    selectorElement.classList.add('font-bold');
    selectorElement.classList.remove('font-normal');
    // image border
    selectorElement.lastElementChild.classList.add('border');
    selectorElement.lastElementChild.classList.add('border-shopify-brand');
    selectorElement.lastElementChild.classList.remove('border-0');
    selectorElement.lastElementChild.classList.remove('border-transparent');
  }

  unhighlightSelectorElement(selectorElement) {
    // button text
    selectorElement.classList.add('font-normal');
    selectorElement.classList.remove('font-bold');
    // image border
    selectorElement.lastElementChild.classList.add('border-0');
    selectorElement.lastElementChild.classList.add('border-transparent');
    selectorElement.lastElementChild.classList.remove('border');
    selectorElement.lastElementChild.classList.remove('border-shopify-brand');
  }

  activateNextButton(handle) {
    this.nextButtonElement.href = handle;
    this.nextButtonElement.classList.remove('not-checked-next-button');
    this.nextButtonElement.classList.add('c-btn--primary');
  }

  resetNextButton() {
    this.nextButtonElement.href = 'javascript:void(0)';
    this.nextButtonElement.classList.add('not-checked-next-button');
    this.nextButtonElement.classList.remove('c-btn--primary');
  }

  setLoadingElement() {
    this.loadingElement.addEventListener('picker-loading', this.showLoadingElement());
    this.loadingElement.addEventListener('picker-done-loading', this.hideLoadingElement());
  }

  hideLoadingElement() {
    this.loadingElement.style.display = 'none';
  }

  showLoadingElement() {
    this.loadingElement.style.display = 'flex';
  }
}

function gatherOptionsWithMetafields(options, optionMetafields) {
  return options.map((o) => {
    const metafieldForOption = optionMetafields.find((m) => m.id == o.id);
    return { ...o, metafieldsCustom: metafieldForOption.metafieldsCustom };
  });
}

function getChoicePreSelection() {
  const urlParams = new URLSearchParams(window.location.search);
  const choicePreSelection = urlParams.get('pre_selection');
  if (choicePreSelection) {
    return choicePreSelection;
  }
}
